<script setup>
// The locked page (PRIVACY_PLAN.md §2b, §4.1). Not an error page: the visitor
// is told what is private and why, offered the one thing they can do about it
// (a signed-out member signs in; a signed-in non-member joins; a stranger on a
// profile asks to follow), and shown the harmless facts the stub still carries
// (how many play here, which sport, since when). The copy, the action order and
// the facts come from utils/privateAccess.js, which is unit-tested.
import {computed} from 'vue';
import {useAuthService} from '~/composables/useAuthService';
import {privateActions, privateCopy} from '~/utils/privateAccess';

const props = defineProps({
  // 'club' | 'competition' | 'fixture' | 'player'
  reason: {type: String, required: true},
  // What the thing is called in the sentence: "ladder", "box league", "meet"…
  entity: {type: String, default: 'competition'},
  name: {type: String, default: ''},
  clubName: {type: String, default: ''},
  // A visible join link's slug, when joining from this page is allowed.
  joinSlug: {type: String, default: null},
  joinLabel: {type: String, default: 'Join'},
  // Whether a Request to join is on offer (the club accepts applications).
  requestToJoin: {type: Boolean, default: false},
  // Set for a player page: the followStatus the API gave for this person.
  followStatus: {type: String, default: null},
  // A members-only club that is not listed: name and logo only (D5).
  unlisted: {type: Boolean, default: false},
  // A player with no account: a club admin answers follow requests for them.
  managed: {type: Boolean, default: false},
  followBusy: {type: Boolean, default: false},
  // Facts from utils/privateAccess (clubFacts / competitionFacts / fixtureFacts).
  facts: {type: Array, default: () => []},
  // Heading over the facts, e.g. "What goes on here".
  factsTitle: {type: String, default: 'At a glance'},
  // An admin previewing the page as a visitor (?viewAs=visitor): signed-out
  // copy, and the buttons shown but not live.
  preview: {type: Boolean, default: false},
});
const emit = defineEmits(['follow', 'request']);
const {isAuthenticated} = useAuthService();
const route = useRoute();

const signedIn = computed(() => !props.preview && isAuthenticated.value);
const loginTo = computed(() => ({path: '/login', query: {authRestricted: 'true', redirectUrl: route.path}}));
const copy = computed(() => privateCopy({
  reason: props.reason,
  entity: props.entity,
  name: props.name,
  clubName: props.clubName,
  signedIn: signedIn.value,
  followStatus: props.followStatus,
  unlisted: props.unlisted,
  managed: props.managed,
}));
const actions = computed(() => privateActions({
  reason: props.reason,
  signedIn: signedIn.value,
  joinSlug: props.joinSlug,
  joinLabel: props.joinLabel,
  requestToJoin: props.requestToJoin,
  followStatus: props.followStatus,
  loginTo: loginTo.value,
}));
const press = (a) => {
  if (props.preview) return;
  if (a.key === 'follow') emit('follow');
  else if (a.key === 'request') emit('request');
};
</script>

<template>
  <div class="tl-card pn">
    <div class="pn-head">
      <div class="pn-icon">
        <v-icon size="24" color="primary">mdi-lock-outline</v-icon>
      </div>
      <div class="pn-text">
        <h2 class="pn-title">{{ copy.title }}</h2>
        <p v-if="copy.body" class="pn-body">{{ copy.body }}</p>
        <p v-if="copy.hint" class="pn-hint">{{ copy.hint }}</p>
      </div>
    </div>

    <div v-if="actions.length" class="pn-actions">
      <v-btn
          v-for="a in actions"
          :key="a.key"
          :color="a.primary ? 'primary' : undefined"
          :variant="a.primary ? 'flat' : 'outlined'"
          rounded="pill"
          :prepend-icon="a.icon"
          :to="preview ? undefined : a.to"
          :disabled="a.disabled || preview"
          :loading="a.key === 'follow' && followBusy"
          @click="press(a)"
      >
        {{ a.label }}
      </v-btn>
    </div>

    <template v-if="facts.length && !unlisted">
      <div class="pn-facts-head">{{ factsTitle }}</div>
      <div class="pn-facts">
        <div v-for="f in facts" :key="f.key" class="pn-fact" :class="{'pn-fact--text': f.text}">
          <v-icon size="18" class="pn-fact-ic">{{ f.icon }}</v-icon>
          <div class="pn-fact-body">
            <div class="pn-fact-value">{{ f.value }}</div>
            <div class="pn-fact-label">{{ f.label }}</div>
            <div v-if="f.detail" class="pn-fact-detail">{{ f.detail }}</div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
.pn {
  padding: 22px 22px 20px;
}

.pn-head {
  display: flex;
  gap: 14px;
  align-items: flex-start;
}

.pn-icon {
  flex: 0 0 48px;
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: rgb(var(--v-theme-brandTint));
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.pn-text {
  min-width: 0;
}

.pn-title {
  font-size: 18px;
  font-weight: 800;
  letter-spacing: -0.4px;
  line-height: 1.25;
}

.pn-body {
  font-size: 13.5px;
  color: rgb(var(--v-theme-charcoal));
  margin: 6px 0 0;
  line-height: 1.65;
  max-width: 560px;
}

.pn-hint {
  font-size: 12.5px;
  color: rgb(var(--v-theme-textSubtle));
  margin: 6px 0 0;
  max-width: 560px;
}

.pn-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
  padding-left: 62px;
}

.pn-facts-head {
  margin-top: 22px;
  padding-top: 16px;
  border-top: 1px solid rgb(var(--v-theme-line));
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: rgb(var(--v-theme-textSubtle));
}

.pn-facts {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 12px 18px;
  margin-top: 10px;
}

.pn-fact {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  min-width: 0;
}

.pn-fact-ic {
  color: rgb(var(--v-theme-textSubtle));
  margin-top: 3px;
}

.pn-fact-body {
  min-width: 0;
}

.pn-fact-value {
  font-size: 20px;
  font-weight: 800;
  letter-spacing: -0.4px;
  line-height: 1.15;
  overflow-wrap: anywhere;
}

.pn-fact--text .pn-fact-value {
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0;
  line-height: 1.35;
}

.pn-fact-label {
  font-size: 11.5px;
  color: rgb(var(--v-theme-textSubtle));
  margin-top: 1px;
}

.pn-fact-detail {
  font-size: 11.5px;
  color: rgb(var(--v-theme-charcoal));
  margin-top: 2px;
}

@media (max-width: 600px) {
  .pn {
    padding: 18px 16px 16px;
  }
  .pn-actions {
    padding-left: 0;
  }
  .pn-actions .v-btn {
    flex: 1 1 100%;
  }
  .pn-facts {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
